/**
 * Lykke's own page: /lykke is the conversation about all campaigns, /lykke/:campaignId one campaign's conversation.
 * Left: the person's conversations and their active campaigns (orders being set up included). Right: the conversation
 * (LykkeConversation, variant page). A conversation started on the home continues here.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, MessageSquare } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { lykkeApi } from './lykkeApi';
import { HOME, useLykke } from './LykkeContext';
import { LykkeConversation } from './LykkeConversation';
import { LykkeAvatar } from './LykkeAvatar';

export function LykkePage({ campaignId }: { campaignId?: string | null }) {
  const l = useLykke();
  const navigate = useNavigate();
  const scope = campaignId || HOME;
  const threads = useQuery({ queryKey: ['lykke', 'threads'], queryFn: () => lykkeApi.threads(), enabled: !!l?.available, staleTime: 15_000, retry: 1 });
  const home = useQuery({ queryKey: ['lykke', 'home'], queryFn: () => lykkeApi.home(), enabled: !!l?.available, staleTime: 30_000, retry: 1 });
  if (!l?.available) return null;
  const campaignThreads = (threads.data?.threads || []).filter((t) => t.campaignId);
  const withThread = new Set(campaignThreads.map((t) => t.campaignId));
  const others = (home.data?.campaigns || []).filter((c) => !c.orderId && !withThread.has(c.id));
  const orders = (home.data?.campaigns || []).filter((c) => c.orderId);
  const title = campaignId ? l.title(campaignId) || campaignThreads.find((t) => t.campaignId === campaignId)?.title || 'This campaign' : 'All your campaigns';
  const row = (active: boolean) => cn('flex w-full flex-col rounded-xl px-3 py-2 text-left transition-colors', active ? 'bg-[#be43ff]/8' : 'hover:bg-[#f9fafb]');

  return (
    <div className="mx-auto grid h-[var(--ws-h,calc(100vh-3.5rem))] w-full max-w-[1280px] grid-cols-1 gap-5 px-4 py-5 md:px-6 lg:grid-cols-[280px_minmax(0,1fr)]" data-lykke-page>
      <aside className="hidden min-h-0 flex-col gap-4 overflow-y-auto rounded-[20px] bg-white p-3 shadow-sm lg:flex" aria-label="Conversations">
        <div>
          <div className="px-2 pb-1 text-[12px] font-semibold uppercase tracking-[0.04em] text-[#98a2b3]">Conversations</div>
          <button type="button" onClick={() => navigate('/lykke')} className={row(!campaignId)}>
            <span className="text-[13.5px] font-semibold text-[#101828]">All your campaigns</span>
            <span className="truncate text-[12px] text-[#667085]">{threads.data?.threads.find((t) => !t.campaignId)?.lastText || 'Ask anything'}</span>
          </button>
          {campaignThreads.map((t) => (
            <button key={t.id} type="button" onClick={() => navigate(`/lykke/${t.campaignId}`)} className={row(t.campaignId === campaignId)}>
              <span className="truncate text-[13.5px] font-semibold text-[#101828]">{t.title}</span>
              <span className="truncate text-[12px] text-[#667085]">{t.lastText || 'No messages yet'}</span>
            </button>
          ))}
        </div>
        {(others.length > 0 || orders.length > 0) && (
          <div>
            <div className="px-2 pb-1 text-[12px] font-semibold uppercase tracking-[0.04em] text-[#98a2b3]">Ask about a campaign</div>
            {orders.map((c) => (
              <button key={c.orderId} type="button" onClick={() => navigate(`/campaigns/new?order=${encodeURIComponent(c.orderId!)}`)} className={row(false)}>
                <span className="truncate text-[13px] font-medium text-[#101828]">{c.name}</span>
                <span className="text-[12px] text-[#667085]">{c.stage}{c.next ? `, ${c.next.what} ${c.next.label}` : ''}</span>
              </button>
            ))}
            {others.map((c) => (
              <button key={c.id} type="button" onClick={() => navigate(`/lykke/${c.id}`)} className={row(c.id === campaignId)}>
                <span className="truncate text-[13px] font-medium text-[#101828]">{c.name}</span>
                <span className="text-[12px] text-[#667085]">{c.stage}</span>
              </button>
            ))}
          </div>
        )}
      </aside>

      <section className="flex min-h-0 flex-col rounded-[20px] bg-white p-4 shadow-sm md:p-6" aria-label="Lykke">
        <header className="mb-2 flex items-center gap-3 border-b border-[#f2f4f7] pb-3">
          <LykkeAvatar size={36} live={l.voice !== 'off'} />
          <div className="min-w-0 flex-1 leading-tight">
            <h1 className="truncate text-[17px] font-semibold text-[#101828]" style={{ fontFamily: 'var(--font-display)' }}>{title}</h1>
            <div className="flex items-center gap-1 text-[12.5px] text-[#667085]"><MessageSquare className="h-3.5 w-3.5" />Lykke, your agent at Beatly</div>
          </div>
          {campaignId && (
            <button type="button" onClick={() => navigate(`/campaigns/${campaignId}/overview`)} className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#d0d5dd] px-3 py-1.5 text-[12.5px] font-semibold text-[#344054] hover:bg-[#f9fafb]">
              Open campaign<ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          )}
        </header>
        <div className="min-h-0 flex-1">
          <LykkeConversation key={scope} scope={scope} variant="page" autoFocus
            placeholder={campaignId ? 'Ask about this campaign' : 'Ask about a campaign, a creator\'s draft, your results…'}
            emptyHint={campaignId ? 'Ask about this campaign: creators, drafts, the brief, dates, results.' : 'Ask about your campaigns, content or brand. Lykke can also take you to any page.'} />
        </div>
      </section>
    </div>
  );
}
